/**
 * "Assumir" do Hoje e do atendimento (spec 018): o ticket passa a ser meu
 * (PATCH responsável — a MESMA escrita do layout Completo, FR-006) e a
 * conversa pelo POST /chats/:id/assume de sempre.
 *
 * Edge case da spec: atendente em pausa (015). A conversa já pergunta — a API
 * recusa com 409 attendant_paused. O ticket não tem essa regra na API, então a
 * pergunta é feita aqui, antes: assumir trabalho em pausa deixaria o relatório
 * contando pausa e atendimento ao mesmo tempo.
 */
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, Spinner } from '@/components/ui/misc';
import { useAuthUser } from '@/auth/use-auth';
import type { ApiError } from '@/lib/api-client';
import { api } from '@/lib/api-client';
import { useAssumeChat } from '@/features/chats/chats-mutations';
import { myPresenceQuery } from '@/features/presence/presence-queries';
import { useSetMyPresence } from '@/features/presence/presence-mutations';
import { errorMessage } from '@/features/tickets/ticket-format';
import { WORK_KEY } from './work-queries';

type Target =
  | { kind: 'ticket'; ticketNumber: number }
  /** `takeover`: a conversa está com OUTRA pessoa (chat.manage_others, spec 013) */
  | { kind: 'chat'; chatId: number; takeover?: boolean };

/**
 * Hook do Assumir com a pergunta da pausa embutida. `assume(target, onDone)`
 * resolve sozinho: sem pausa, assume; em pausa, abre o diálogo e só segue se a
 * pessoa escolher encerrar a pausa. `dialog` precisa ser renderizado.
 */
export function useAssumeWork(): {
  assume: (target: Target, onDone?: () => void) => void;
  pending: boolean;
  error: string | null;
  dialog: ReactNode;
} {
  const me = useAuthUser();
  const qc = useQueryClient();
  const presenceQ = useQuery(myPresenceQuery());
  const setPresence = useSetMyPresence();
  const assumeChat = useAssumeChat();
  const [paused, setPaused] = useState<{ target: Target; onDone?: () => void } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assumeTicket = async (ticketNumber: number) => {
    if (!me) throw new Error('sem usuário');
    const res = await api(`/tickets/${ticketNumber}`, { method: 'PATCH', body: { responsibleId: me.id } });
    if (res.error) throw res.error;
    void qc.invalidateQueries({ queryKey: ['erp', 'tickets'] });
  };

  const run = async (target: Target, endPause: boolean, onDone?: () => void) => {
    setPending(true);
    setError(null);
    try {
      if (target.kind === 'ticket') {
        if (endPause) await setPresence.mutateAsync({ status: 'available' });
        await assumeTicket(target.ticketNumber);
      } else {
        try {
          await assumeChat.mutateAsync({
            chatId: target.chatId,
            ...(target.takeover ? { takeover: true } : {}),
            ...(endPause ? { endPause: true } : {}),
          });
        } catch (e) {
          const err = e as ApiError;
          // o status local pode estar velho: a API é quem sabe da pausa
          if (!endPause && err.status === 409 && err.error === 'attendant_paused') {
            setPaused({ target, ...(onDone ? { onDone } : {}) });
            return;
          }
          throw e;
        }
      }
      setPaused(null);
      void qc.invalidateQueries({ queryKey: WORK_KEY });
      onDone?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending(false);
    }
  };

  const assume = (target: Target, onDone?: () => void) => {
    if (presenceQ.data?.status === 'paused') {
      setPaused({ target, ...(onDone ? { onDone } : {}) });
      return;
    }
    void run(target, false, onDone);
  };

  const dialog = paused ? (
    <Dialog open onClose={() => setPaused(null)} title="Você está em pausa">
      <p className="text-sm text-muted-foreground">
        Encerrar a pausa e assumir? Seu status passa a Disponível agora.
      </p>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setPaused(null)}>
          Continuar em pausa
        </Button>
        <Button size="sm" disabled={pending} onClick={() => void run(paused.target, true, paused.onDone)}>
          {pending ? <Spinner className="size-4 border-primary-foreground" /> : null}
          Encerrar a pausa e assumir
        </Button>
      </div>
    </Dialog>
  ) : null;

  return { assume, pending, error, dialog };
}
